/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useMemo } from 'react';
import { Checkbox, IconButton, Stack, TableBody, TableRow, Tooltip } from '@mui/material';
import CustomTableCell from 'components/cmn/CustomTableCell';
import { ICmnTableBodyProps } from 'interface/cmn/commonInfo';
import { ProjectUserContent } from 'interface/project/projectInfo';
import { getComparatorAny, hanldeFormatDate, stableSort } from 'libs/mindUtils';
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Preview';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAppDispatch } from 'store';
import { getIndividualPdfResultDownload, deleteUserSave } from 'store/project/projectSlice';
import { openBackdrop, closeBackdrop } from 'store/backdrop/backdropSlice';
import { showMessage } from 'store/message/messageSlice';
import { openDialog } from 'store/dialog/dialogSlice';
import { SHOW_ADD1_DUPLICATE } from 'constants/features';
import UserDeleteConfirmDialog from './UserDeleteConfirmDialog';

interface IManagementTableBodyProps extends ICmnTableBodyProps<ProjectUserContent> {
  selected: readonly ProjectUserContent[];
  setSelected: React.Dispatch<React.SetStateAction<readonly ProjectUserContent[]>>;
  type: 'admin' | 'manager';
  projectId: string;
  mutate: () => void;
  projectType?: string;
}

function ManagementTableBody({
  selected,
  setSelected,
  loading,
  rows,
  type,
  projectId,
  mutate,
  projectType,
}: IManagementTableBodyProps): JSX.Element {
  const dispatch = useAppDispatch();
  const isSelected = (id: string) => selected.some(obj => obj.id === id);

  // 정렬된 rows 배열을 미리 계산
  const sortedRows = useMemo(() => {
    return stableSort(rows, getComparatorAny('desc', 'id'));
  }, [rows]);

  const add1DuplicateInfos = useMemo(() => {
    if (!SHOW_ADD1_DUPLICATE) {
      return [];
    }

    const totalByAdd1: Record<string, number> = {};
    const currentByAdd1: Record<string, number> = {};

    sortedRows.forEach(row => {
      if (row.add1) {
        totalByAdd1[row.add1] = (totalByAdd1[row.add1] || 0) + 1;
      }
    });

    return sortedRows.map(row => {
      if (!row.add1) {
        return null;
      }

      const total = totalByAdd1[row.add1];
      if (total <= 1) {
        return null;
      }

      const current = (currentByAdd1[row.add1] || 0) + 1;
      currentByAdd1[row.add1] = current;

      return { current, total };
    });
  }, [sortedRows]);

  const handleClick = (event: React.MouseEvent<unknown>, _row: ProjectUserContent) => {
    const selectedIndex = selected.findIndex(item => item.id === _row.id);
    let newSelected: readonly ProjectUserContent[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, _row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handlePdfDownload = async (partCode: string, userName: string) => {
    dispatch(openBackdrop({}));
    try {
      const response = await dispatch(
        getIndividualPdfResultDownload({
          projectId,
          partCode,
        }),
      ).unwrap();
      const { status } = response;
      if (status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `진단결과_${userName}_${partCode}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        dispatch(showMessage({ message: '다운로드가 완료되었습니다.', variant: 'success' }));
      } else {
        dispatch(showMessage({ message: '다운로드에 실패했습니다.', variant: 'warning' }));
      }
    } catch (error) {
      console.error(error);
      dispatch(showMessage({ message: '다운로드 중 오류가 발생했습니다.', variant: 'error' }));
    } finally {
      dispatch(closeBackdrop());
    }
  };

  const handlePdfPreview = async (partCode: string) => {
    dispatch(openBackdrop({}));
    try {
      const response = await dispatch(
        getIndividualPdfResultDownload({
          projectId,
          partCode,
        }),
      ).unwrap();
      const { status } = response;
      if (status === 200) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        // URL은 일정 시간 후 정리 (탭이 닫혀도 메모리 누수 방지)
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);
        dispatch(showMessage({ message: '새 탭에서 열립니다.', variant: 'success' }));
      } else {
        dispatch(showMessage({ message: '미리보기에 실패했습니다.', variant: 'warning' }));
      }
    } catch (error) {
      console.error(error);
      dispatch(showMessage({ message: '미리보기 중 오류가 발생했습니다.', variant: 'error' }));
    } finally {
      dispatch(closeBackdrop());
    }
  };

  const handleInspectionStart = (partCode: string) => {
    const frontendUrl = window.location.origin;
    const inspectionUrl = `${frontendUrl}/mind/projects/${projectId}/${partCode}`;
    window.open(inspectionUrl, '_blank');
  };

  const handleDeleteUser = (userId: string, userName: string, userEmail: string) => {
    dispatch(openBackdrop({}));
    dispatch(deleteUserSave({ projectId, userId }))
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          dispatch(showMessage({ message: response?.data?.message || '참여자가 삭제되었습니다.', variant: 'success' }));
          mutate();
        } else {
          dispatch(
            showMessage({
              message: response?.data?.message || '참여자 삭제에 실패했습니다.',
              variant: 'error',
            }),
          );
        }
      })
      .catch(e => {
        console.log(e);
        dispatch(showMessage({ message: '참여자 삭제 중 오류가 발생했습니다.', variant: 'error' }));
      })
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, row: ProjectUserContent) => {
    event.stopPropagation();
    dispatch(
      openDialog({
        content: (
          <UserDeleteConfirmDialog
            userName={row.userName}
            userEmail={row.userEmail}
            handleConfirm={() => handleDeleteUser(row.id, row.userName, row.userEmail)}
          />
        ),
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
      }),
    );
  };
  return (
    <>
      {!loading && rows.length === 0 && (
        <TableBody>
          <TableRow>
            <CustomTableCell align="center" colSpan={13} sx={{ border: 'none' }}>
              조회된 데이터가 없습니다.
            </CustomTableCell>
          </TableRow>
        </TableBody>
      )}
      {!loading && rows.length !== 0 && (
        <TableBody>
          {sortedRows.map((row, index) => {
            const isItemSelected = isSelected(row.id);
            const labelId = `table-checkbox-${index}`;
            const add1DuplicateInfo = add1DuplicateInfos[index];
            return (
              <TableRow
                hover
                role="checkbox"
                aria-checked={isItemSelected}
                tabIndex={-1}
                key={row.id}
                selected={isItemSelected}
                id={labelId}
                sx={{ cursor: 'pointer' }}
              >
                {type === 'admin' && (
                  <CustomTableCell align="center" component="th" id={labelId} scope="row" padding="none">
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                      inputProps={{
                        'aria-labelledby': labelId,
                      }}
                      onClick={event => handleClick(event, row)}
                    />
                  </CustomTableCell>
                )}
                <CustomTableCell align="center" sx={{ pl: '12px!important' }}>
                  {row.userName}
                </CustomTableCell>
                <CustomTableCell align="center">{row.userCompName}</CustomTableCell>
                <CustomTableCell align="center">{row.userEmail}</CustomTableCell>
                <CustomTableCell align="center">
                  {row.add1 || '-'}
                  {SHOW_ADD1_DUPLICATE && add1DuplicateInfo && (
                    <span
                      style={{
                        color: row.isDone === 'Y' ? 'red' : 'blue',
                        marginLeft: '4px',
                      }}
                    >
                      ({add1DuplicateInfo.current}/{add1DuplicateInfo.total})
                    </span>
                  )}
                </CustomTableCell>
                <CustomTableCell align="center">{row.partCode || '-'}</CustomTableCell>
                <CustomTableCell align="center">{row.isDone === 'Y' ? '실시완료' : '미실시'}</CustomTableCell>
                <CustomTableCell align="center">
                  <Stack direction="row" justifyContent="center">
                    <Tooltip title="검사 시작">
                      <span>
                        <IconButton onClick={() => handleInspectionStart(row.partCode)}>
                          <PlayArrowIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {projectType !== 'DC' && (
                      <>
                        <Tooltip title="다운로드">
                          <span>
                            <IconButton
                              disabled={row.isDone === 'N'}
                              onClick={() => handlePdfDownload(row.partCode, row.userName)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="미리보기">
                          <span>
                            <IconButton disabled={row.isDone === 'N'} onClick={() => handlePdfPreview(row.partCode)}>
                              <PreviewIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </CustomTableCell>
                <CustomTableCell align="center">{hanldeFormatDate(row.sendEmailAt)}</CustomTableCell>
                <CustomTableCell align="center">{hanldeFormatDate(row.regAt)}</CustomTableCell>
                <CustomTableCell align="center">{hanldeFormatDate(row.startAt)}</CustomTableCell>
                <CustomTableCell align="center">{row.progressStep || '-'}</CustomTableCell>
                <CustomTableCell align="center">{hanldeFormatDate(row.doneAt)}</CustomTableCell>
                <CustomTableCell align="center">
                  <IconButton onClick={e => handleDeleteClick(e, row)} color="error" size="small">
                    <DeleteOutlineIcon />
                  </IconButton>
                </CustomTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      )}
    </>
  );
}

export default memo(ManagementTableBody);
